import {
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  SimpleGrid,
  Tag,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { CalendarBlank, Flame, Lightning, Lock, TreeStructure, Truck, User } from "@phosphor-icons/react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../../config";
import { QUESTIONNAIRE_STATUSES } from "../../../../../utils";
import { useGetSourceDataTasks } from "../../../../../hooks/query";

const DIMENSION_LEVEL_KEYS = ["dimensionOne", "dimensionTwo", "dimensionThree", "dimensionFour", "dimensionFive"];

// Convert the Insights dimension filter map ({ dimensionOne: ["12","13"], ... }) into the
// pre-serialized query fragment ("dimensionOne=12,13&dimensionTwo=7") the backend binds to
// List<Long> per level. Empty levels dropped. Mirrors the copy in each detail page.
const dimensionItemsFragment = (dimension) =>
  DIMENSION_LEVEL_KEYS.filter((k) => (dimension?.[k]?.length || 0) > 0)
    .map((k) => `${k}=${dimension[k].join(",")}`)
    .join("&");

const SCOPE_COLORS = {
  scope1: { light: "#E8FAF0", text: "#0B5E30", icon: Flame, label: "Scope 1" },
  scope2: { light: "#E8F4FD", text: "#1A5276", icon: Lightning, label: "Scope 2" },
  scope3: { light: "#F3E8FE", text: "#5B1D99", icon: Truck, label: "Scope 3" },
};

const STATUS_CONFIG = {
  [QUESTIONNAIRE_STATUSES.NEW]: { progress: 15, color: "#FC8181", label: "No data" },
  [QUESTIONNAIRE_STATUSES.IN_PROGRESS]: { progress: 50, color: "#FBD38D", label: "In Progress" },
  [QUESTIONNAIRE_STATUSES.FINALIZED]: { progress: 100, color: "#B8E086", label: "Finalized" },
};

// Scope 3 category config flag → category number, in category order (matches TaskCard.jsx).
const SCOPE3_CAT_FIELDS = [
  ["purchasedGoodsAndServicesShouldBeFilled", "1"],
  ["capitalGoodsShouldBeFilled", "2"],
  ["fuelAndEnergyRelatedActivitiesShouldBeFilled", "3"],
  ["upstreamTransportationAndDistributionShouldBeFilled", "4"],
  ["wasteGeneratedInOperationsShouldBeFilled", "5"],
  ["businessTravelShouldBeFilled", "6"],
  ["employeeCommutingShouldBeFilled", "7"],
  ["downstreamTransportationAndDistributionShouldBeFilled", "9"],
  ["useOfSoldProductsShouldBeFilled", "11"],
  ["treatmentOfSoldProductsShouldBeFilled", "12"],
  ["franchisesShouldBeFilled", "14"],
  ["investmentsShouldBeFilled", "15"],
];

// Derive the scope pills from the task's real configuration flags (same as DataEntryPlanning's
// TaskCard) rather than a pre-joined string.
const parseScopeTags = (configurations) => {
  if (!configurations) return [];
  const tags = [];
  if (configurations.directEmissionsShouldBeFilled) tags.push({ ...SCOPE_COLORS.scope1 });
  if (configurations.indirectEmissionsShouldBeFilled) tags.push({ ...SCOPE_COLORS.scope2 });
  const cats = SCOPE3_CAT_FIELDS.filter(([field]) => configurations[field]).map(([, n]) => n);
  if (cats.length > 0) tags.push({ ...SCOPE_COLORS.scope3, categories: cats });
  return tags;
};

// Resolve each questionnaire-level dimension item id to its display name using the org structure,
// so the card shows "Head Office" rather than an id. Skips levels with no item set.
const resolveDimensionLabels = (dimensionItems, organizationStructure) => {
  if (!dimensionItems || !organizationStructure?.dimensions) return [];
  const keyToField = {
    dimensionOne: "dimensionOneItem",
    dimensionTwo: "dimensionTwoItem",
    dimensionThree: "dimensionThreeItem",
    dimensionFour: "dimensionFourItem",
    dimensionFive: "dimensionFiveItem",
  };
  const labels = [];
  DIMENSION_LEVEL_KEYS.forEach((key) => {
    const itemId = dimensionItems[keyToField[key]];
    if (!itemId) return;
    const dim = organizationStructure.dimensions[key];
    const item = dim?.items?.find((i) => i.id === itemId);
    if (item) labels.push(item.name);
  });
  return labels;
};

const SourceTaskCard = ({ task, dimensionLabels, onOpen }) => {
  const config = STATUS_CONFIG[task.status] || STATUS_CONFIG[QUESTIONNAIRE_STATUSES.NEW];
  const scopeTags = parseScopeTags(task.configurations);
  const isFinalized = task.status === QUESTIONNAIRE_STATUSES.FINALIZED;
  const title = task.description || [task.period?.year, task.period?.quarter !== "ALL" ? task.period?.quarter : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card
      w="100%"
      borderWidth="1px"
      borderRadius="lg"
      borderColor="gray.200"
      boxShadow="sm"
      bg="white"
      cursor="pointer"
      _hover={{ borderColor: "lime" }}
      transition="all 0.15s"
      onClick={() => onOpen(task.id)}
    >
      <Flex justify="space-between" align="center" bg="gray.50" px={5} py={3} borderBottom="1px solid" borderColor="gray.100" borderTopRadius="lg">
        <Wrap spacing={1.5}>
          {scopeTags.map((scope, idx) => (
            <WrapItem key={idx}>
              <HStack spacing={0}>
                <Tag
                  size="sm"
                  borderRadius="full"
                  bg={scope.light}
                  color={scope.text}
                  fontSize="11px"
                  fontWeight="600"
                  px={2}
                  gap={1}
                  borderRightRadius={scope.categories ? 0 : "full"}
                >
                  <scope.icon size={12} weight="fill" />
                  {scope.label}
                </Tag>
                {scope.categories && (
                  <Tag size="sm" borderRadius="full" borderLeftRadius={0} bg={scope.light} color={scope.text} fontSize="11px" fontWeight="400" px={2} opacity={0.8}>
                    · Cat {scope.categories.join(", ")}
                  </Tag>
                )}
              </HStack>
            </WrapItem>
          ))}
        </Wrap>
      </Flex>

      <CardBody px={5} py={4} display="flex" flexDirection="column" flex={1}>
        <Text fontSize="15px" fontWeight="600" color="gray.800" lineHeight="1.4" noOfLines={2} mb={2}>
          {title}
        </Text>
        <HStack spacing={3} mb={3} flexWrap="wrap" rowGap={1}>
          <HStack spacing={1.5}>
            <CalendarBlank size={13} color="#A0AEC0" />
            <Text fontSize="13px" color="gray.500">
              {task.period?.year}
              {task.period?.quarter && task.period.quarter !== "ALL" ? ` ${task.period.quarter}` : ""}
            </Text>
          </HStack>
          {dimensionLabels.map((label, idx) => (
            <HStack key={idx} spacing={1.5}>
              <TreeStructure size={13} color="#A0AEC0" />
              <Text fontSize="13px" color="gray.500">{label}</Text>
            </HStack>
          ))}
          {task.assigneeName && (
            <Tag size="sm" borderRadius="md" bg="#FFF8E1" px={2} py={0.5} gap={1.5}>
              <User size={12} color="#718096" />
              <Text fontSize="11px" fontWeight="600" color="gray.700" textTransform="uppercase">{task.assigneeName}</Text>
            </Tag>
          )}
        </HStack>
        <Box mt="auto" pt={3} maxW="50%" minW="120px">
          <Flex alignItems="center" gap={1.5} mb={1}>
            <Text fontSize="xs" fontWeight="600" color="gray.600">{config.label}</Text>
            {isFinalized && <Lock size={12} color="#718096" weight="fill" />}
          </Flex>
          <Box h="10px" borderRadius="full" bg="gray.100" overflow="hidden">
            <Box h="100%" w={`${config.progress}%`} borderRadius="full" bg={config.color} />
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
};

/**
 * Real "Source Data" section for the Insights Scope 1/2/3 drill-downs. Lists the FINALIZED
 * data-collection tasks that have entered data for `scope` under the current year / quarter /
 * dimension filter. Clicking a card opens the questionnaire (view mode, since finalized).
 *
 * @param scope a ScopeCategoryKey string (e.g. "SCOPE_1", "SCOPE_2", "CAPITAL_GOODS").
 */
const SourceDataTasks = ({ scope, year, quarter, dimension, organizationStructure }) => {
  const navigate = useNavigate();

  const dimensionItemIds = useMemo(() => dimensionItemsFragment(dimension), [dimension]);

  const { data: tasks, isFetching } = useGetSourceDataTasks(
    { scope, year, quarter: quarter || "ALL", dimensionItemIds },
    { enabled: !!scope && !!year, cacheTime: 60_000, keepPreviousData: true }
  );

  const openQuestionnaire = (id) => navigate(`${routes.organisationQuestionnaire}?id=${id}&view=true`);

  return (
    <Box>
      <Flex align="baseline" gap={3} mb={3} flexWrap="wrap">
        <Text fontSize="22px" fontWeight="700" color="darkTeal">Source Data</Text>
        <Text fontSize="body.xs" color="gray.500">
          Finalised data-collection tasks with data for this scope, year, quarter and dimension.
        </Text>
      </Flex>
      {isFetching && !tasks ? (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
          <Text textAlign="center" color="gray.400" fontSize="13px">Loading…</Text>
        </Box>
      ) : !tasks || tasks.length === 0 ? (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">
            No finalised data-collection tasks with data for this selection.
          </Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
          {tasks.map((task) => (
            <SourceTaskCard
              key={task.id}
              task={task}
              dimensionLabels={resolveDimensionLabels(task.dimensionItems, organizationStructure)}
              onOpen={openQuestionnaire}
            />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default SourceDataTasks;
